import { isOnPrem } from '@/utils/buildProfile';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { useQuery } from '@tanstack/react-query';

/** Optional on-prem components an admin can install or remove per host. */
export interface DeploymentComponents {
  interpreter: boolean;
  evidence: boolean;
}

export interface DeploymentCapabilities {
  components: DeploymentComponents;
  isPending: boolean;
}

const ALL_COMPONENTS: DeploymentComponents = { interpreter: true, evidence: true };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Only a literal `available: false` removes a component. */
const isRemoved = (capabilities: Record<string, unknown>, name: string): boolean => {
  const entry = capabilities[name];
  return isRecord(entry) && entry.available === false;
};

/**
 * Reads `{"capabilities": {name: {"available": bool}}}`, the shape of
 * `GET /chat/capabilities` and `GET /translator/capabilities`. Unlike those
 * egress capabilities this fails open: a malformed body or a missing name
 * leaves the existing flag / entitlement / licence gates in charge, so a
 * frontend newer than its backend hides nothing.
 */
export function resolveDeploymentComponents(response: unknown): DeploymentComponents {
  if (!isRecord(response) || !isRecord(response.capabilities)) return ALL_COMPONENTS;
  const { capabilities } = response;
  return {
    interpreter: !isRemoved(capabilities, 'interpreter'),
    evidence: !isRemoved(capabilities, 'evidence'),
  };
}

const CLOUD_CAPABILITIES: DeploymentCapabilities = {
  components: ALL_COMPONENTS,
  isPending: false,
};

// Picks up an admin's toggle (the backend restarts with the new set) without
// a reload: a stale answer refetches on the next mount or window focus. No
// refetchInterval, which runs one timer per observer and this hook has many.
const STALE_TIME_MS = 60_000;
// A stalled backend must not hold the gates in their loading state.
const REQUEST_TIMEOUT_MS = 10_000;

export function useCloudDeploymentCapabilities(): DeploymentCapabilities {
  return CLOUD_CAPABILITIES;
}

function useOnPremDeploymentCapabilities(): DeploymentCapabilities {
  const { data, isPending } = useQuery({
    queryKey: ['deployment-capabilities'],
    queryFn: async () => {
      const { data } = await scribeHttpV2.get<unknown>('/deployment/capabilities', {
        timeout: REQUEST_TIMEOUT_MS,
      });
      return resolveDeploymentComponents(data);
    },
    staleTime: STALE_TIME_MS,
    // The route exists or it never will (older backend): fail open at once.
    retry: false,
  });

  // Fail open: a backend without the route (older release) or a failed fetch
  // leaves the existing flag / entitlement / licence gates in charge, as
  // before this hook existed. React Query keeps the last good answer across a
  // backend restart's failed refetches.
  return { components: data ?? ALL_COMPONENTS, isPending };
}

/**
 * Which optional components this deployment serves
 * (`GET /deployment/capabilities`, on-prem only). Feature gates AND it in so
 * a component an admin removed disappears from the UI.
 *
 * Cloud serves every component and never asks: `isOnPrem` is build-time
 * folded, so cloud bundles get the constant and no request.
 */
export const useDeploymentCapabilities: () => DeploymentCapabilities = isOnPrem
  ? useOnPremDeploymentCapabilities
  : useCloudDeploymentCapabilities;
